import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { List } from 'immutable';
import { isEqual, isUndefined } from 'lodash';
import {
  DropdownHorizontalAlignmentsEnum,
  DropdownVerticalAlignmentsEnum,
} from '../../../shared/components/dropdown/dropdown.component';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import {
  ITrackingAction,
  ITrackingObject,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { FormAlignmentsEnum } from '../../form-master.enum';
import { CustomFormFieldComponent } from '../custom-form-field/custom-form-field.component';
import { ISelectOption } from '../select-field/select-field.interface';
import { DurationPickerFieldBuilder } from './duration-picker-field.builder';

// TODO: review duration picker, should we just wrap a select-field? should we add text input-field?
@Component({
  selector: 'app-duration-picker-field',
  templateUrl: 'duration-picker-field.component.html',
  styleUrls: ['duration-picker-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [DurationPickerFieldBuilder],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DurationPickerFieldComponent),
      multi: true,
    },
  ],
})
export class DurationPickerFieldComponent
  extends CustomFormFieldComponent
  implements ControlValueAccessor, OnInit, OnChanges {
  @Input() alignment: FormAlignmentsEnum;
  @Input() label: string;
  @Input() hideLabel: boolean;
  @Input() description: string;
  @Input() fieldId: string;
  @Input() condensed: boolean;
  @Input() placeholder: string;
  @Input() secondsIncrement: number;
  @Input() format: string;
  @Input() blocker: (selectOption: ISelectOption) => Promise<ISelectOption>;
  @Input() icon: IIcon;
  @Input() e2eId: string;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingTapped: ITrackingAction;
  @Input() trackingSelected: ITrackingAction;
  @Input() trackingAdditionalProperties: any;
  @Input() horizontalAlignment: DropdownHorizontalAlignmentsEnum =
    DropdownHorizontalAlignmentsEnum.left;
  @Input() verticalAlignment: DropdownVerticalAlignmentsEnum =
    DropdownVerticalAlignmentsEnum.bottom;
  @Input() dynamicVAlignment: boolean;
  @Input() formControl: AbstractControl;

  @Output() onClickIcon: EventEmitter<void> = new EventEmitter<void>();

  selectedOption: ISelectOption;
  isDisabled: boolean;
  values: List<ISelectOption>;
  dropdownValues: List<List<ISelectOption>>;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _trackingDispatcher: TrackingDispatcher,
    private _durationPickerFieldBuilder: DurationPickerFieldBuilder,
    translateService: TranslateService
  ) {
    super(_changeDetectorRef, translateService);
  }

  ngOnChanges(changes) {
    if (changes.secondsIncrement || changes.format) {
      this.values = this._durationPickerFieldBuilder.buildValues(
        this.secondsIncrement,
        this.format
      );
      this.dropdownValues = List([this.values]);
    }
  }

  ngOnInit() {
    this.fieldControl = this.formControl;

    super.ngOnInit();
  }

  writeValue(value: any) {
    if (!isUndefined(value) && this.values) {
      let newOption: ISelectOption = this.values.find(
        (selectOption: ISelectOption) => isEqual(value, selectOption.value)
      );

      if (!newOption) {
        newOption = this._durationPickerFieldBuilder.buildValue(
          value,
          this.format
        );
      }

      this.setSelectedOption(newOption);
    } else if (this.placeholder) {
      this.setSelectedOption({ key: this.placeholder, value: '' });
    }
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;

    this._changeDetectorRef.markForCheck();
  }

  onSelect(selectOption: ISelectOption) {
    if (
      this.blocker &&
      (isUndefined(selectOption.block) || selectOption.block)
    ) {
      this.blocker(selectOption).then((result: ISelectOption) => {
        if (
          result &&
          (this.selectedOption
            ? !isEqual(this.selectedOption.value, result.value)
            : true)
        ) {
          this.propagateChange(result.value);

          this.setSelectedOption(result);

          this.trackOnSelect(result);
        }
      });
    } else if (
      this.selectedOption
        ? !isEqual(this.selectedOption.key, selectOption.key)
        : true
    ) {
      this.propagateChange(selectOption.value);

      this.setSelectedOption(selectOption);

      this.trackOnSelect(selectOption);
    }
  }

  setSelectedOption(optionValue: ISelectOption) {
    this.selectedOption = optionValue;

    this.formControl.markAsDirty();

    this._changeDetectorRef.markForCheck();
  }

  trackOnSelect(optionValue: ISelectOption) {
    if (
      this.trackingObject &&
      this.trackingSelected &&
      optionValue.trackingValue
    ) {
      this._trackingDispatcher.trackEvent(
        this.trackingObject,
        this.trackingSelected.setValue(optionValue.trackingValue),
        this.trackingAdditionalProperties
      );
    }
  }
}
